/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { message, Row, Col } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { pick } from 'lodash/fp';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type CtsModuleSettingDataFragment } from '../../../../../api/fragments/CtsModuleSettingData';
import { type CtsModuleSpecsFragment } from '../../../../../api/fragments/CtsModuleSpecs';
import {
    UpdateCtsModuleSettingDocument,
    type UpdateCtsModuleSettingMutation,
    type UpdateCtsModuleSettingMutationVariables,
} from '../../../../../api/mutations/updateCtsModuleSetting';
import { type CtsSettingUpdateInput, type FinderVehicleCondition, ModuleType } from '../../../../../api/types';
import { useCompany } from '../../../../../components/contexts/CompanyContextManager';
import { useDealerContext } from '../../../../../components/contexts/DealerContextManager';
import CTSFinderModuleSelectField from '../../../../../components/fields/CTSFinderModuleSelectField';
import DealershipPriceDisclaimerField from '../../../../../components/fields/DealershipFields/DealershipPriceDisclaimer';
import Form from '../../../../../components/fields/Form';
import InputField from '../../../../../components/fields/InputField';
import SelectField from '../../../../../components/fields/SelectField';
import SwitchField from '../../../../../components/fields/SwitchField';
import useHandleError from '../../../../../utilities/useHandleError';
import useSystemSwitchData from '../../../../../utilities/useSystemSwitchData';
import useValidator from '../../../../../utilities/useValidator';
import MarketTypeGroup from '../../modules/implementations/shared/MarketTypeGroup';
import { getFinancingApplicationMarket } from '../../StandardApplicationModulePage/StandardApplicationModuleMainDetails';
import usePriceDisclaimerToolTipByMarket from '../../usePriceDisclaimerToolTipByMarket';
import { updateValidator } from './validators';

type CtsSettingTabMainDetailsProps = {
    module: CtsModuleSpecsFragment;
    setting: CtsModuleSettingDataFragment;
};

const StyledContainer = styled(Row)`
    background-color: #fff;
    padding: 20px;
`;

type FieldsProps = {
    insuranceModuleId: string;
    initialInsuranceValue?: string;
    bankModuleId: string;
    vehicleModuleIds: string[];
    setFinderApplicationModuleCondition: Dispatch<SetStateAction<FinderVehicleCondition[]>>;
};

const Fields = ({
    insuranceModuleId: initialInsuranceValue,
    bankModuleId,
    vehicleModuleIds,
    setFinderApplicationModuleCondition,
}: FieldsProps) => {
    const {
        values: { market },
    } = useFormikContext<CtsSettingUpdateInput>();

    const company = useCompany(true);
    const { dealersFromApi } = useDealerContext();
    const { yesNoSwitch } = useSystemSwitchData();

    const availableInsuranceModules = useMemo(() => {
        if (!company?.modules) {
            return [];
        }

        const insuranceModuleList = company.modules.filter(module => module.__typename === 'InsuranceModule');

        return insuranceModuleList.map(insuranceModule => ({
            value: insuranceModule.id,
            label: insuranceModule.displayName,
        }));
    }, [company]);

    const priceDisclaimerToolTip = usePriceDisclaimerToolTipByMarket(market);
    const priceDisclaimerItemProps = useMemo(() => ({ tooltip: priceDisclaimerToolTip }), [priceDisclaimerToolTip]);
    const { t } = useTranslation(['moduleDetails']);

    return (
        <StyledContainer gutter={10}>
            <Col md={8} xs={24}>
                <InputField
                    {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                    name="displayName"
                    required
                />
            </Col>
            <Col md={8} xs={24}>
                <InputField
                    {...t('moduleDetails:fields.ctsSettingId', { returnObjects: true })}
                    name="identifier"
                    required
                />
            </Col>
            <Col md={8} xs={24}>
                <DealershipPriceDisclaimerField
                    {...t('moduleDetails:fields.priceDisclaimer', { returnObjects: true })}
                    dealers={dealersFromApi}
                    itemProps={priceDisclaimerItemProps}
                    name="priceDisclaimer"
                />
            </Col>
            <Col md={8} xs={24}>
                <DealershipPriceDisclaimerField
                    {...t('moduleDetails:fields.preownedPriceDisclaimer', { returnObjects: true })}
                    dealers={dealersFromApi}
                    itemProps={priceDisclaimerItemProps}
                    name="preownedPriceDisclaimer"
                />
            </Col>
            <MarketTypeGroup dealers={dealersFromApi} name="market" required />
            <Col md={8} xs={24}>
                <SelectField
                    {...t('moduleDetails:fields.insuranceModuleId', { returnObjects: true })}
                    allowClear={!initialInsuranceValue}
                    disabled={!!initialInsuranceValue}
                    name="insuranceModuleId"
                    options={availableInsuranceModules}
                    showSearch
                />
            </Col>
            <Col md={8} xs={24}>
                <CTSFinderModuleSelectField
                    {...t('moduleDetails:fields.finderModules', {
                        returnObjects: true,
                    })}
                    ctsSettingProps={{
                        bankModuleId,
                        vehicleModuleIds,
                    }}
                    mode="multiple"
                    moduleTypes={[ModuleType.FinderApplicationPublicModule]}
                    name="finderModuleIds"
                    setFinderApplicationModuleCondition={setFinderApplicationModuleCondition}
                />
            </Col>

            <Col md={8} xs={24}>
                <SwitchField
                    {...t('moduleDetails:fields.isActive', {
                        returnObjects: true,
                    })}
                    name="isActive"
                    {...yesNoSwitch}
                />
            </Col>
        </StyledContainer>
    );
};

const CtsSettingTabMainDetails = ({ module, setting }: CtsSettingTabMainDetailsProps) => {
    const { t } = useTranslation('ctsModuleDetails');

    const initialValues = useMemo<CtsSettingUpdateInput>(
        () => ({
            ...pick(['displayName', 'market', 'insuranceModuleId', 'identifier', 'isActive'], setting),
            priceDisclaimer: setting.dealerPriceDisclaimer,
            preownedPriceDisclaimer: setting.preownedDealerPriceDisclaimer,
            marketType: getFinancingApplicationMarket(setting.marketType),
            finderModuleIds: (setting.finderModules || []).map(
                module => module.__typename === 'FinderApplicationPublicModule' && module.id
            ),
        }),
        [setting]
    );

    const [finderApplicationModuleCondition, setFinderApplicationModuleCondition] = useState<FinderVehicleCondition[]>(
        setting.finderModules.flatMap(
            module => module.__typename === 'FinderApplicationPublicModule' && module.finderVehicleConditions
        ) || []
    );

    const validate = useValidator(updateValidator(finderApplicationModuleCondition));

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<CtsSettingUpdateInput>(
        async values => {
            message.loading({
                content: t('ctsModuleDetails:settings.update.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            const settings = values;

            if (settings.marketType.singapore && !settings.marketType.singapore.coe.overrides) {
                settings.marketType.singapore.coe.overrides = [];
            }

            if (settings.marketType.newZealand) {
                if (!settings.marketType.newZealand.ppsr.overrides) {
                    settings.marketType.newZealand.ppsr.overrides = [];
                }
                if (!settings.marketType.newZealand.estFee.overrides) {
                    settings.marketType.newZealand.estFee.overrides = [];
                }
            }

            // submit creation
            await apolloClient
                .mutate<UpdateCtsModuleSettingMutation, UpdateCtsModuleSettingMutationVariables>({
                    mutation: UpdateCtsModuleSettingDocument,
                    variables: {
                        moduleId: module.id,
                        settingId: setting.id,
                        settings,
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('ctsModuleDetails:settings.update.messages.success'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, setting.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit, errors }) => (
                <Form id="updateMainDetails" name="updateMainDetails" onSubmitCapture={handleSubmit}>
                    <Fields
                        bankModuleId={setting.bankModuleId}
                        insuranceModuleId={initialValues.insuranceModuleId}
                        setFinderApplicationModuleCondition={setFinderApplicationModuleCondition}
                        vehicleModuleIds={setting.vehicleModuleIds}
                    />
                </Form>
            )}
        </Formik>
    );
};

export default CtsSettingTabMainDetails;
